import { Button, Grid, Modal, Typography } from "antd";
import { Check, Pencil, X } from "lucide-react";
import type { ApplicationItem } from "@/types/Application/application";
import { safeFormatyyyyMMddDateTime } from "@/utils/formatDate";
import InfoItem from "@/components/application/InfoItem";
import { formatValue } from "@/utils/formatValue";
import { profileFields, residentFields } from "@/constants/application";

interface DetailsApplicationModalProps {
  open: boolean;
  selectedRow: ApplicationItem | null;
  detailData: Partial<ApplicationItem> | null;
  onClose: () => void;
  onEdit: () => void;
  onAccept: () => void;
  onReject: () => void;
  showActionButtons?: boolean;
}

const { Title } = Typography;
const { useBreakpoint } = Grid;

const resolveResidentFieldValue = (
  detailData: Partial<ApplicationItem> | null,
  selectedRow: ApplicationItem | null,
  key: keyof ApplicationItem,
) => {
  const normalValue =
    detailData?.[key] ?? selectedRow?.[key] ?? "-";

  if (key === "occupationType") {
    const occupationType = String(
      detailData?.occupationType ?? selectedRow?.occupationType ?? "",
    ).toUpperCase();

    if (occupationType === "LAIN-LAIN") {
      return detailData?.occupation ?? selectedRow?.occupation ?? normalValue;
    }
  }

  if (key === "houseStatus") {
    const houseStatus = String(
      detailData?.houseStatus ?? selectedRow?.houseStatus ?? "",
    ).toUpperCase();

    if (houseStatus === "LAIN-LAIN") {
      return detailData?.houseDetails ?? selectedRow?.houseDetails ?? normalValue;
    }
  }

  return normalValue;
};

const modalStyles = {
  container: { padding: 0 },
  header: { padding: 0, marginBottom: 0 },
  body: { padding: 24 },
  footer: { padding: "0 16px 16px" },
} as const;

export function DetailsApplicationModal({
  open,
  selectedRow,
  detailData,
  onClose,
  onEdit,
  onAccept,
  onReject,
  showActionButtons = true,
}: DetailsApplicationModalProps) {
  const screens = useBreakpoint();

  const modalItemTitle = (
    <>
      <div className="flex items-center justify-between p-4 sm:p-6 bg-[#E5F2FF] rounded-tl-2xl rounded-tr-2xl">
        <div className="flex items-center gap-4 sm:gap-6">
          <Title
            level={1}
            style={{ margin: 0, fontSize: screens.xs ? "22px" : "28px" }}
          >
            {detailData?.villageName ?? selectedRow?.villageName ?? "-"}
          </Title>
          <span className="text-sm sm:text-lg font-bold bg-white rounded-lg p-4 h-11 leading-none inline-flex items-center justify-center">
            {detailData?.registrationCode ??
              selectedRow?.registrationCode ??
              "-"}
          </span>
        </div>

        <div className="flex items-center gap-4 sm:gap-6">
          <span className="text-sm sm:text-lg capitalize font-bold border-[#000000] border rounded-lg p-4 h-11 leading-none inline-flex items-center justify-center">
            {detailData?.residencyStatus ?? selectedRow?.residencyStatus ?? "-"}
          </span>

          <div className="text-right">
            <span className="block text-xs font-semibold text-[#666] leading-none">
              Tarikh pendaftaran
            </span>
            <div className="text-sm sm:text-lg font-bold">
              {safeFormatyyyyMMddDateTime(
                detailData?.submittedOn ?? selectedRow?.submittedOn,
              ) ?? "-"}
            </div>
          </div>

          <button type="button" className="cursor-pointer" onClick={onClose}>
            <X size={28} aria-hidden="true" />
          </button>
        </div>
      </div>
      {showActionButtons ? (
        <div className="text-center py-1.5 bg-[#FECD39] text-lg font-bold">
          Sedang Diproses
        </div>
      ) : (
        <div className="text-center py-1.5 bg-[#1F993B] text-lg font-bold text-white">
          Lulus
        </div>
      )}
    </>
  );

  const modalButton = showActionButtons ? (
    <div className="grid grid-cols-2 gap-10 w-full">
      <Button
        type="primary"
        size="large"
        icon={<X size={18} aria-hidden="true" strokeWidth={2.5} />}
        onClick={onReject}
        style={{ backgroundColor: "#C00" }}
        className="w-full"
      >
        Tolak
      </Button>

      <Button
        type="primary"
        size="large"
        icon={<Check size={18} aria-hidden="true" strokeWidth={2.5} />}
        style={{ backgroundColor: "#1F993B" }}
        className="w-full"
        onClick={onAccept}
      >
        Terima
      </Button>
    </div>
  ) : null;

  return (
    <Modal
      closable={false}
      title={modalItemTitle}
      open={open}
      width={1200}
      onCancel={onClose}
      styles={modalStyles}
      footer={modalButton}
      centered
    >
      {selectedRow && (
        <>
          <div className="flex justify-between mb-8 items-center gap-4">
            <div>
              <span className="text-sm text-[#666] leading-none font-semibold">
                Nama
              </span>
              <div className="text-sm sm:text-lg font-bold w-3xl">
                {detailData?.name || selectedRow.name}
              </div>
            </div>

              <Button
                htmlType="button"
                type="primary"
                style={{ height: 48, borderRadius: 8, minWidth: 161 }}
                icon={<Pencil size={18} aria-hidden="true" />}
                onClick={onEdit}
              >
                KEMASKINI
              </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 sm:gap-10">
            {profileFields.map((field) => (
              <div
                key={field.key}
                className={field.colSpan === 2 ? "sm:col-span-2" : ""}
              >
                <InfoItem
                  label={field.label}
                  value={formatValue(
                    detailData?.[field.key] ?? selectedRow[field.key],
                  )}
                />
              </div>
            ))}
          </div>

          <div className="bg-[#F4F8FF] grid sm:grid-cols-4 gap-6 sm:gap-10 p-4 mt-8">
            {residentFields.map(([label, key]) => (
              <InfoItem
                key={key}
                label={label}
                value={formatValue(
                  resolveResidentFieldValue(detailData, selectedRow, key),
                )}
              />
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}